import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { DashboardShell } from '../../components/DashboardShell.js';
import { useAuthStore } from '../../lib/auth.js';
import {
  useCurrentOrg,
  useOrgCredits,
  useOrgDetails,
  useOrgEvents,
  useStripeRefresh,
} from '../../lib/orgs.js';
import { api, ApiClientError } from '../../lib/api.js';
import { OnboardingPage } from './OnboardingPage.js';
import { trackCreditsPurchase } from '../../lib/analytics.js';

const CREDITS_PENDING_KEY = 'boletra_credits_pending';

export function DashboardHomePage() {
  const { lang } = useParams();
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const org = useCurrentOrg();
  const [params, setParams] = useSearchParams();
  const stripeRefresh = useStripeRefresh(org?.id ?? null);

  // Cuando el usuario vuelve de Stripe Hosted Onboarding (?stripe=ok),
  // pullamos estado autoritario en lugar de confiar en que el webhook
  // `account.updated` ya nos haya pegado. Idempotente del lado del server.
  const stripeParam = params.get('stripe');
  useEffect(() => {
    if (!org?.id) return;
    if (stripeParam !== 'ok' && stripeParam !== 'retry') return;
    stripeRefresh.mutate(undefined, {
      onSettled: () => {
        const next = new URLSearchParams(params);
        next.delete('stripe');
        setParams(next, { replace: true });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id, stripeParam]);

  // Cuando el usuario vuelve de pagar créditos en Stripe (?credits=ok),
  // disparamos la conversión de Google Ads con el paquete guardado en
  // sessionStorage antes de redirigir.
  const creditsParam = params.get('credits');
  useEffect(() => {
    if (creditsParam !== 'ok') return;
    try {
      const raw = sessionStorage.getItem(CREDITS_PENDING_KEY);
      if (raw) {
        const pending = JSON.parse(raw) as {
          packageId: string;
          credits: number;
          totalUsdCents: number;
        };
        trackCreditsPurchase(pending);
        sessionStorage.removeItem(CREDITS_PENDING_KEY);
      }
    } catch {
      // sessionStorage no disponible o JSON inválido — best effort
    }
  }, [creditsParam]);

  if (!org) return <OnboardingPage />;

  return (
    <DashboardShell>
      <Banner
        stripeFlag={stripeParam}
        creditsFlag={params.get('credits')}
        refreshResult={stripeRefresh.data}
        refreshing={stripeRefresh.isPending}
      />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <p className="text-sm text-neutral-500">{tCommon('brand.tagline')}</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">
            {t('home.welcome', { name: user?.name || user?.email || '' })}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/${lang}/dashboard/events/new`} className="btn-primary">
            {t('events.create_button')}
          </Link>
          <Link to={`/${lang}/dashboard/credits`} className="btn-secondary">
            {t('credits.buy_button')}
          </Link>
        </div>
      </div>

      <ReviewAlert orgId={org.id} />

      <StripeAlert orgId={org.id} />

      <Stats orgId={org.id} />
    </DashboardShell>
  );
}

function Banner({
  stripeFlag,
  creditsFlag,
  refreshResult,
  refreshing,
}: {
  stripeFlag: string | null;
  creditsFlag: string | null;
  refreshResult: { stripeOnboardingComplete: boolean; pendingVerification: boolean } | undefined;
  refreshing: boolean;
}) {
  const { t } = useTranslation('dashboard');
  if (creditsFlag === 'ok') {
    return <BannerBox color="emerald">{t('home.banner.credits_ok')}</BannerBox>;
  }
  if (creditsFlag === 'cancel') {
    return <BannerBox color="amber">{t('home.banner.credits_cancel')}</BannerBox>;
  }
  if (stripeFlag === 'retry') {
    return <BannerBox color="amber">{t('home.banner.stripe_retry')}</BannerBox>;
  }
  if (stripeFlag === 'ok') {
    if (refreshing || !refreshResult) {
      return <BannerBox color="neutral">{t('home.banner.stripe_verifying')}</BannerBox>;
    }
    if (refreshResult.stripeOnboardingComplete) {
      return <BannerBox color="emerald">{t('home.banner.stripe_ok')}</BannerBox>;
    }
    if (refreshResult.pendingVerification) {
      return <BannerBox color="amber">{t('home.banner.stripe_pending')}</BannerBox>;
    }
    return <BannerBox color="amber">{t('home.banner.stripe_missing_fields')}</BannerBox>;
  }
  return null;
}

function BannerBox({
  color,
  children,
}: {
  color: 'emerald' | 'amber' | 'neutral';
  children: React.ReactNode;
}) {
  const palette =
    color === 'emerald'
      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
      : color === 'amber'
        ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
        : 'bg-neutral-800/50 border-neutral-700 text-neutral-300';
  return <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${palette}`}>{children}</div>;
}

/**
 * Aviso de estado de revisión de la org. Mientras está `pending` no puede
 * publicar eventos ni vender; `rejected` queda bloqueada. Aprobada → no muestra
 * nada. Es el feedback para el promotor del gate anti-fraude del backend.
 */
function ReviewAlert({ orgId }: { orgId: string }) {
  const { t } = useTranslation('dashboard');
  const details = useOrgDetails(orgId);
  if (details.isLoading || !details.data) return null;
  const status = details.data.reviewStatus;
  if (status === 'approved') return null;

  const rejected = status === 'rejected';
  return (
    <div
      className={`card mb-6 border ${
        rejected ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'
      }`}
    >
      <h2 className={`font-semibold mb-1 ${rejected ? 'text-red-300' : 'text-amber-200'}`}>
        {rejected ? t('home.review.rejected_title') : t('home.review.pending_title')}
      </h2>
      <p className="text-sm text-neutral-400 max-w-2xl">
        {rejected ? t('home.review.rejected_body') : t('home.review.pending_body')}
      </p>
    </div>
  );
}

function StripeAlert({ orgId }: { orgId: string }) {
  const { t } = useTranslation('dashboard');
  const details = useOrgDetails(orgId);
  const refresh = useStripeRefresh(orgId);
  const mutation = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/orgs/${orgId}/stripe/onboarding`, {
        method: 'POST',
      }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  if (details.isLoading) return null;
  // Si ya está conectada y verificada, no mostramos nada. Mantener este
  // banner permanente es lo que confundía al usuario después del onboarding.
  if (details.data?.stripeOnboardingComplete) return null;

  // Diferenciamos "nunca conectaste" de "conectaste pero Stripe sigue
  // verificando" mostrando un texto distinto cuando hay accountId.
  const hasAccount = Boolean(details.data?.stripeAccountId);
  const verifyingAfterOnboarding = hasAccount && refresh.data?.pendingVerification;

  return (
    <div className="card mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <div>
        <h2 className="font-semibold mb-1">
          {hasAccount ? t('home.stripe.verifying_title') : t('home.stripe.connect_title')}
        </h2>
        <p className="text-sm text-neutral-400 max-w-2xl">
          {hasAccount ? t('home.stripe.verifying_body') : t('home.stripe.connect_body')}
        </p>
        {mutation.isError && (
          <p className="text-red-400 text-sm mt-2">{(mutation.error as ApiClientError).message}</p>
        )}
        {refresh.isError && <p className="text-red-400 text-sm mt-2">{refresh.error.message}</p>}
        {verifyingAfterOnboarding && !refresh.isPending && (
          <p className="text-amber-300 text-xs mt-2">{t('home.stripe.still_verifying_note')}</p>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        {hasAccount && (
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="btn-secondary whitespace-nowrap text-sm"
          >
            {refresh.isPending ? t('home.stripe.rechecking') : t('home.stripe.recheck')}
          </button>
        )}
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="btn-primary whitespace-nowrap"
        >
          {mutation.isPending
            ? t('home.stripe.opening')
            : hasAccount
              ? t('home.stripe.continue_onboarding')
              : t('home.stripe.connect_button')}
        </button>
      </div>
    </div>
  );
}

function Stats({ orgId }: { orgId: string }) {
  const { t } = useTranslation('dashboard');
  const credits = useOrgCredits(orgId);
  const events = useOrgEvents(orgId);
  const liveCount = events.data?.events.filter((e) => e.status === 'live').length ?? 0;
  const draftCount = events.data?.events.filter((e) => e.status === 'draft').length ?? 0;
  const balance = credits.data?.balance ?? 0;
  const freeRemaining = credits.data
    ? Math.max(0, credits.data.freeMonthlyAllowance - credits.data.freeMonthlyConsumed)
    : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <Stat
        label={t('home.stats.live_events')}
        value={String(liveCount)}
        sub={t('home.stats.drafts_sub', { count: draftCount })}
      />
      <Stat
        label={t('home.stats.total_credits')}
        value={balance.toLocaleString()}
        sub={t('home.stats.free_remaining_sub', { count: freeRemaining })}
      />
      <Stat
        label={t('home.stats.free_credits_left')}
        value={freeRemaining.toLocaleString()}
        sub={t('home.stats.free_renews_sub')}
      />
      <Stat
        label={t('home.stats.issuable_total')}
        value={(balance + freeRemaining).toLocaleString()}
        sub={t('home.stats.issuable_sub')}
      />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="text-2xl sm:text-3xl font-bold mt-2 break-all">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}
